import React, {
   CSSProperties,
   FocusEventHandler,
   forwardRef, KeyboardEventHandler,
   MouseEventHandler,
   PropsWithChildren,
   ReactElement,
   ReactNode,
   Ref,
   RefAttributes, useCallback,
   useImperativeHandle, useMemo,
   useRef, useState
} from 'react';
import AntdSelect from 'antd/lib/select';
import {
   OptionsType
} from './Option';
import { convertSize } from '../../../utils/StyleUtils';
import {getFilterOptionHandler, mapOptionChildren, mapOptions} from './SelectUtils';

export type SingleType<MixType> = MixType extends (infer Single)[]
                                  ? Single
                                  : MixType;

export type RawValue = string | number;

export interface LabeledValue {
   key?: string;
   value: RawValue;
   label: ReactNode;
}

export type DefaultValue =
   | RawValue
   | RawValue[]
   | LabeledValue
   | LabeledValue[];

export type CustomTagProps = {
   label: DefaultValue;
   value: DefaultValue;
   disabled: boolean;
   onClose: (event?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
   closable: boolean;
};

/**
 * {@docCategory Select}
 */
export interface SelectProps<ValueType extends DefaultValue = DefaultValue> {

   /**
    * The id of the select component
    */
   id?: string;

   /**
    * The className of the select component
    */
   className?: string;

   /**
    * The inline style of the select component
    */
   style?: CSSProperties;

   /**
    * The custom suffix icon.  Overrides the arrow/chevron.
    */
   suffixIcon?: ReactNode;

   /**
    *	Size of Select input
    *	@defaultvalue middle
    */
   size?: 'small' | 'middle' | 'large' | undefined;

   /**
    * Whether has border style
    * @defaultvalue true
    */
   bordered?: boolean;

   // Options
   /**
    * Select options. Use this property to get better performance than jsx
    * child definitions.
    */
   options?: OptionsType;

   // Value
   /**
    * Current selected option
    */
   value?: ValueType;

   /**
    * Initial selected option
    */
   defaultValue?: ValueType;

   // Dropdown
   /**
    * Controlled open state of dropdown
    */
   open?: boolean;

   /**
    * Initial open state of dropdown
    */
   defaultOpen?: boolean;

   /**
    * Config popup height
    */
   listHeight?: number;

   /**
    * Config popup list item height
    */
   listItemHeight?: number;

   /**
    * The inline style of dropdown menu
    */
   dropdownStyle?: CSSProperties;

   /**
    * The className of dropdown menu
    */
   dropdownClassName?: string;

   /**
    * Determine whether the dropdown menu and the select input are the same width.
    * Default set min-width same as input. Will ignore when value less than select
    * width. false will disable virtual scroll
    * @defaultvalue true
    */
   dropdownMatchSelectWidth?: boolean | number;


   /**
    * Customize dropdown content
    */
   dropdownRender?: (menu: React.ReactElement) => React.ReactElement;

   /**
    * Parent Node which the selector should be rendered to. Default to body. When position issues
    * happen, try to modify it into scrollable content and position it relative
    * @param triggerNode - The trigger node
    * @defaultvalue () =\> document.body;
    */
   getPopupContainer?: (triggerNode: unknown) => HTMLElement;

   // Others
   /**
    * Whether or not the Select component is disabled
    * @defaultvalue false
    */
   disabled?: boolean;

   /**
    * Indicate loading state
    * @defaultvalue false
    */
   loading?: boolean;

   /**
    * Get focus by default
    * @defaultvalue false
    */
   autoFocus?: boolean;

   /**
    * Whether active first option by default
    * @defaultvalue true
    */
   defaultActiveFirstOption?: boolean;

   /**
    * Specify content to show when no result matches
    * @defaultvalue 'Not Found'
    */
   notFoundContent?: ReactNode;

   /**
    * The placeholder text for the Select component
    */
   placeholder?: ReactNode;

   /**
    * Which prop value of option will render as content of the select
    */
   optionLabelProp?: string;

   /**
    * Which prop value of option will be used for filter if filterOption is true. If options
    * is set, it should be set to label
    */
   optionFilterProp?: string;

   /**
    * The tab index of this component
    */
   tabIndex?: number;

   /**
    * Sets the width.
    */
   width?: string | number;

   /**
    * Sets the min width.
    */
   minWidth?: string | number;

   /**
    * Sets the max width.
    */
   maxWidth?: string | number;

   /**
    * Whether or not to show the search feature
    */
   showSearch?: boolean;

   /**
    * The current input "search" text
    */
   searchValue?: string;

   /**
    * Whether to show the drop-down arrow
    */
   showArrow?: boolean;

   /**
    * Whether or not to allow clear
    */
   allowClear?: boolean;

   /**
    * The custom clear icon to use when allowClear option is set
    */
   clearIcon?: React.ReactNode;

   /**
    * Set the mode of Select
    */
   mode?: 'multiple' | 'tags';

   /**
    * Separator used to tokenize on tag and multiple mode
    */
   tokenSeparators?: string[];

   /**
    * Customize tag render
    * @param props - The custom tag props
    */
   tagRender?: (props: CustomTagProps) => React.ReactElement;

   /**
    * Max tag text length to show
    */
   maxTagTextLength?: number;

   /**
    * Max tag count to show.
    */
   maxTagCount?: number;

   /**
    * Placeholder for not showing tags
    */
   maxTagPlaceholder?: React.ReactNode | ((omittedValues: LabeledValue[]) => React.ReactNode);

   /**
    * If true, filter options by input, if function, filter options against it. The function will
    * receive two arguments, inputValue and option, if the function returns true, the option will
    * be included in the filtered set; Otherwise, it will be excluded
    */
   filterOption?: boolean | ((inputValue: string, option?: OptionsType[number]) => boolean);

   /**
    * Sort function for search options sorting, see Array.sort's compareFunction
    * @param optionA - The first option to compare
    * @param optionB - The second option to compare
    */
   filterSort?: (optionA: OptionsType[number], optionB: OptionsType[number]) => number;

   // Events
   /**
    * Callback called when the dropdown visibility changes
    * @param open - Whether the dropdown is open or not
    */
   onDropdownVisibleChange?: (open: boolean) => void;

   /**
    * Callback called when an option is selected
    * @param value - The value being selected
    * @param option - The option object
    */
   onSelect?: (value: SingleType<ValueType>, option: OptionsType[number]) => void;

   /**
    * Callback called when an option is deselected
    * @param value - The value being deselected
    * @param option - The option object
    */
   onDeselect?: (value: SingleType<ValueType>, option: OptionsType[number]) => void;

   /**
    * Callback called when the component is clicked.
    * @param event - The mouse event
    */
   onClick?: MouseEventHandler;

   /**
    * Callback called when the value is changed.
    * @param value - The value
    * @param option - The option object
    */
   onChange?: (value: ValueType, option: OptionsType[number] | OptionsType) => void;

   /**
    * Callback called when the user is searching for a value
    *
    * @param value - The value to search
    */
   onSearch?: (value: string) => void;

   /**
    * Callback called when the component loses focus.
    * @param event - The focus event object
    */
   onBlur?: FocusEventHandler<HTMLElement>;

   /**
    * Callback called when the component gains focus.
    * @param event - The focus event object
    */
   onFocus?: FocusEventHandler<HTMLElement>;

   /**
    * Callback called when the component gains focus.
    * @param event - The focus event object
    */
   onKeyDown?: KeyboardEventHandler<HTMLElement>;
}

export interface SelectRefProps<ValueType extends DefaultValue = DefaultValue> {
   focus: () => void;
   blur: () => void;
}

export const Select = forwardRef(function Select<ValueType extends DefaultValue = DefaultValue>(props: PropsWithChildren<SelectProps<ValueType>>, ref: Ref<SelectRefProps<ValueType>>) {

   const antdSelectRef = useRef<any>(null);
   const [currentSearchString, setCurrentSearchString] = useState<string | undefined>(props.searchValue);

   useImperativeHandle(ref, () => ({
      focus: () => {
         if (antdSelectRef && antdSelectRef.current) {
            antdSelectRef.current.focus();
         }
      },
      blur: () => {
         if (antdSelectRef && antdSelectRef.current) {
            antdSelectRef.current.blur();
         }
      }
   }));

   const optionFilterProp = props.optionFilterProp || ((props.options !== undefined && !props.children) ? 'label' : 'children');
   const filterOptionHandler = useCallback(getFilterOptionHandler(optionFilterProp),[optionFilterProp]);

   const onSearchProp = props.onSearch;
   const onSearchHandler = useCallback((value: string) => {
      if (onSearchProp) {
         onSearchProp(value);
      }
      setCurrentSearchString(value);
   },[onSearchProp]);

   const onSelectProp = props.onSelect;
   const onSelectHandler = useCallback((value, option) => {
      if (onSelectProp) {
         onSelectProp(value, option);
      }
      setCurrentSearchString(undefined);
   },[onSelectProp]);

   const { width, minWidth, maxWidth, style: styleProp, optionLabelProp, options: optionsProp, children: childrenProp } = props;
   const style = React.useMemo(() => ({
         width: convertSize(width, '250px'),
         minWidth: convertSize(minWidth, '60px'),
         maxWidth: convertSize(maxWidth),
         ...styleProp
   }),[ width, minWidth, maxWidth, styleProp]);

   const optionLabel = optionLabelProp === undefined ? 'label' : optionLabelProp;
   const options = useMemo(() => {
      return (optionsProp && !childrenProp && currentSearchString !== undefined && props.filterOption === undefined ?
                             mapOptions(currentSearchString, optionsProp, optionLabel) : optionsProp);
      },[optionsProp, childrenProp, optionLabel, currentSearchString]);

   return (
      <AntdSelect
         id={props.id}
         className={props.className}
         style={style}
         suffixIcon={props.suffixIcon}
         size={props.size}
         bordered={props.bordered}
         options={options}
         value={props.value}
         defaultValue={props.defaultValue}
         open={props.open}
         defaultOpen={props.defaultOpen}
         listHeight={props.listHeight}
         listItemHeight={props.listItemHeight}
         dropdownStyle={props.dropdownStyle}
         dropdownClassName={props.dropdownClassName}
         dropdownMatchSelectWidth={props.dropdownMatchSelectWidth}
         dropdownRender={props.dropdownRender}
         getPopupContainer={props.getPopupContainer}
         disabled={props.disabled}
         loading={props.loading}
         autoFocus={props.autoFocus}
         defaultActiveFirstOption={props.defaultActiveFirstOption}
         notFoundContent={props.notFoundContent}
         placeholder={props.placeholder}
         optionLabelProp={props.optionLabelProp}
         optionFilterProp={!props.showSearch || props.filterOption !== undefined ? props.optionFilterProp : optionFilterProp}
         tabIndex={props.tabIndex}
         showSearch={props.showSearch}
         searchValue={props.searchValue}
         showArrow={props.showArrow}
         allowClear={props.allowClear}
         clearIcon={props.clearIcon}
         mode={props.mode}
         tokenSeparators={props.tokenSeparators}
         maxTagTextLength={props.maxTagTextLength}
         maxTagCount={props.maxTagCount}
         maxTagPlaceholder={props.maxTagPlaceholder}
         filterOption={!props.showSearch || props.filterOption !== undefined ? props.filterOption : filterOptionHandler}
         filterSort={props.filterSort}
         onDropdownVisibleChange={props.onDropdownVisibleChange}
         onSelect={onSelectHandler}
         onDeselect={props.onDeselect}
         onClick={props.onClick}
         onChange={props.onChange}
         onBlur={props.onBlur}
         onFocus={props.onFocus}
         onKeyDown={props.onKeyDown}
         onSearch={!(props.showSearch || props.mode === 'multiple') ? props.onSearch : onSearchHandler}
         ref={antdSelectRef}
         // @ts-ignore
         tagRender={props.tagRender} // our CustomTagProps is a bit more strict and they use rc-select types that aren't available to cast with
      >
         {(props.children && currentSearchString !== undefined && props.filterOption === undefined) ? mapOptionChildren(currentSearchString, props.children) : props.children}
      </AntdSelect>
   );
}) as <ValueType extends DefaultValue = DefaultValue>(props: PropsWithChildren<SelectProps<ValueType>> & RefAttributes<SelectRefProps<ValueType>>) => ReactElement | null;

